import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Moon, Compass, Shield } from "lucide-react";

const HERO_IMG =
    "https://images.unsplash.com/photo-1633505765486-e404bbbec654?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHw0fHxwcmVtaXVtJTIwY296eSUyMGJlZHJvb20lMjBsaWdodCUyMGJlaWdlfGVufDB8fHx8MTc4ODE4MTg2Mnww&ixlib=rb-4.1.0&q=85";

export default function Welcome() {
    return (
        <div className="min-h-screen bg-brand-beige" data-testid="welcome-page">
            <div className="max-w-6xl mx-auto px-6 py-10 md:py-16">
                <header className="flex items-center justify-between mb-12">
                    <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-xl bg-[#2A84C8] flex items-center justify-center">
                            <Moon className="w-5 h-5 text-white" />
                        </div>
                        <span className="font-heading text-lg text-brand-ink">
                            Le Petit Matelassier
                            <span className="text-brand-ink/50 text-sm ml-2 hidden sm:inline">
                                · La Ciotat
                            </span>
                        </span>
                    </div>
                    <Link
                        to="/admin"
                        className="text-sm text-brand-ink/50 hover:text-[#2A84C8]"
                        data-testid="admin-link"
                    >
                        Espace conseiller
                    </Link>
                </header>

                <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <p className="text-sm uppercase tracking-widest text-[#2A84C8] font-medium mb-4">
                            Diagnostic confort
                        </p>
                        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-brand-ink leading-tight mb-6">
                            Quel matelas est fait pour vous&nbsp;?
                        </h1>
                        <p className="text-lg md:text-xl text-brand-ink/70 mb-10 leading-relaxed max-w-lg">
                            Quelques questions suffisent pour mieux comprendre votre sommeil
                            et vous orienter vers le confort qui vous correspond.
                        </p>

                        <Link
                            to="/questionnaire"
                            className="primary-btn"
                            data-testid="start-questionnaire-btn"
                        >
                            Commencer mon diagnostic confort
                            <span aria-hidden>→</span>
                        </Link>

                        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg">
                            <Feature icon={<Compass className="w-5 h-5" />} title="Environ 3 minutes" desc="Une seule question à la fois, très clair." />
                            <Feature icon={<Shield className="w-5 h-5" />} title="Résultat immédiat" desc="Sans engagement, sans compte requis." />
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="relative"
                    >
                        <div className="rounded-3xl overflow-hidden shadow-xl">
                            <img
                                src={HERO_IMG}
                                alt="Chambre chaleureuse aux tons beige"
                                className="w-full h-[420px] md:h-[520px] object-cover"
                            />
                        </div>
                        <div className="absolute -bottom-6 -left-6 hidden md:block bg-white rounded-2xl shadow-lg p-4 w-64">
                            <p className="text-xs text-brand-ink/60 mb-1">Boutique</p>
                            <p className="font-heading text-lg text-brand-ink">La Ciotat</p>
                            <p className="text-xs text-brand-ink/60 mt-1">
                                Essai en boutique conseillé
                            </p>
                            <a
                                href="tel:+33677548220"
                                className="inline-block mt-2 text-sm text-[#2A84C8] font-medium hover:underline"
                                data-testid="welcome-shop-phone"
                            >
                                06 77 54 82 20
                            </a>
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
}

function Feature({ icon, title, desc }) {
    return (
        <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-white border border-brand-ink/10 flex items-center justify-center text-[#2A84C8] shrink-0">
                {icon}
            </div>
            <div>
                <p className="font-medium text-brand-ink">{title}</p>
                <p className="text-sm text-brand-ink/60">{desc}</p>
            </div>
        </div>
    );
}
